import clsx from 'clsx'
import find from 'lodash/find'
import indexOf from 'lodash/indexOf'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import noop from 'lodash/noop'
import React, { useRef, useState } from 'react'

import SVGAngleLeft from '@Assets/SVG/AngleLeft'
import SVGAngleRight from '@Assets/SVG/AngleRight'
import { ImageV3 } from '@Components/Image'
import { Link } from '@Components/Link'
import { ProductTileV2 } from '@Components/ProductList/ProductTiles/LoadMore/ProductTileV2/ProductTileV2'
import { HTMLSliderElement, Slider, SliderData } from '@Components/Slider'
import { PromptDialog } from '@Domains/Cart/Components/Prompt/Dialog'
import { useAddToCart } from '@Domains/Cart/Hooks/useAddToCart'
import { Product } from '@Generated/graphql'
import { getChipsV2 } from '@Globals/CMS/ProductHelpers'
import { Image } from '@Globals/Image'

import defaultStyles from './ImageWithProductGridSlider.module.scss'
import modalStyles from './ImageWithProductGridSliderModal.module.scss'

interface ImageWithProductGridSliderProps {
  destPageType?: string
  destPageTypeID?: string
  id?: string
  image?: Image
  imageCaption?: string
  imageOnRight?: boolean
  isInModal?: boolean
  link?: string
  linkNewTab?: boolean
  products: Product[]
  showBuyButton?: boolean
  sourceID?: string
}

const ImageForProductGridSlider = ({
  image,
  imageCaption,
  isInModal,
  link,
}: Pick<ImageWithProductGridSliderProps, 'imageCaption' | 'image' | 'link' | 'isInModal'>) => {
  const styles = isInModal ? modalStyles : defaultStyles
  return (
    <>
      {image && (
        <ImageV3
          alt={imageCaption || ''}
          className={styles.image}
          fill
          priority
          quality={100}
          sizes="(max-width: 768px) 100vw, (max-width: 1600px) 600px, 800px"
          src={image?.url}
        />
      )}
      {imageCaption && (
        <div className={styles.captionWrapper}>
          <p className={styles.captionTitle}>Persona 1</p>
          <span>
            <p className={styles.captionText}>{imageCaption}</p>
            {link && (
              <p className={styles.captionLink}>
                Visit Style
              </p>
            )}
          </span>

        </div>
      )}
    </>
  )
}

export const ImageWithProductGridSlider = ({
  destPageType,
  destPageTypeID,
  id,
  image,
  imageCaption,
  imageOnRight,
  isInModal,
  link,
  linkNewTab,
  products,
  showBuyButton,
  sourceID = 'product_grid_image',
}: ImageWithProductGridSliderProps) => {
  const styles = isInModal ? modalStyles : defaultStyles
  const [addToCart, { isLoading, prompts, reset, variables }] = useAddToCart()
  const [addingToCart, setAddingToCart] = useState<string | null>(null)
  const [addedToCart, setAddedToCart] = useState<string[]>([])
  const [numberOfItemsToMove, setNumberOfItemsToMove] = useState(1)
  const sliderRef = useRef<HTMLSliderElement | null>(null)
  const [sliderState, setSliderState] = useState<SliderData | null>(null)

  return (
    <>
      <div
        className={clsx(
          styles.imageWithProductGridSlider,
          imageOnRight && styles.imageOnRight,
          isInModal && styles.inModal,
          !(image && image.url) && styles.noImage,
        )}
        id={id}
      >
        {image && image.url && link && (
          <Link
            analytics={{
              dest_page_type: destPageType || '',
              dest_page_type_id: destPageTypeID || '',
              source_id: sourceID,
            }}
            className={styles.imageWrapper}
            href={link}
            target={linkNewTab ? '_blank' : undefined}
          >
            <ImageForProductGridSlider image={image} imageCaption={imageCaption} isInModal={isInModal} link={link} />
          </Link>
        )}
        {image && image.url && !link && (
          <div className={styles.imageWrapper}>
            <ImageForProductGridSlider image={image} imageCaption={imageCaption} isInModal={isInModal} link={link} />
          </div>
        )}
        <div className={styles.sliderWrapper}>
          <Slider
            className={clsx(
              styles.productGridSlider,
              isInModal && styles.inModal,
            )}
            numberOfItemsToMove={numberOfItemsToMove}
            onSlide={(e, resize) => {
              if (resize) {
                const res = e.numberOfItemsInViewport >= 4 ? e.numberOfItemsInViewport : 1
                setNumberOfItemsToMove(prev => (prev === res ? prev : res))
              }
              if (!e.isScrollable) {
                setSliderState(null)
                return
              }
              setSliderState(e)
            }}
            ref={sliderRef}
          >
            {map(products, (product, index) => {
              const {
                bottomChips,
                topChips,
              } = getChipsV2(product)
              // TODO: need a single way to fetch product tiles, with JUST the needed information, instead of this magic mess everywhere
              const primaryAsset = find(product.assets, asset => indexOf(asset.tags || [], 'primary') !== -1) || product.assets?.[0]
              return (
                <ProductTileV2
                  key={product.id}
                  alreadyAddedToCart={addedToCart.includes(product.id)}
                  bottomChips={bottomChips}
                  className={clsx(
                    styles.product,
                    'product',
                    index === 0 && styles.scrollSnapStart,
                    ((index !== 0 && index % (numberOfItemsToMove - 1) === 0) || (products.length - 1 === index)) && styles.scrollSnapEnd,
                    numberOfItemsToMove === 1 && styles.alwaysSnap,
                  )}
                  imageWidth="200"
                  isOutOfStock={!!product.availability && !product.availability?.isInStock}
                  loadingBuyButton={isLoading && addingToCart === product.id}
                  onBuy={!showBuyButton
                    ? undefined
                    : () => {
                      setAddingToCart(product.id)
                      addToCart([
                        {
                          productID: product.id,
                          quantity: 1,
                        },
                      ])
                        .then((r) => {
                          if (isEmpty(r?.errors)) {
                            setAddedToCart(prev => [...prev, product.id])
                          }
                        })
                    }}
                  originalPrice={product.masterVariant?.price?.format || ''}
                  overwrittenPrice={product.masterVariant?.price?.discount?.format || undefined}
                  primaryImage={primaryAsset?.url || ''}
                  productID={product.id}
                  title={product.name || ''}
                  topChips={topChips}
                  url={product.url || ''}
                />
              )
            })}
          </Slider>

          {sliderState && numberOfItemsToMove && sliderState.isScrollable
              && (
                <p className={styles.slideCount}>
                  {`${Math.ceil((sliderState.lastIndex + 1) / numberOfItemsToMove)}/${Math.ceil(products.length / numberOfItemsToMove)}`}
                </p>
              )}
          {sliderState && sliderState.isScrollable && (
            <button className={clsx(styles.arrow, styles.left)} onClick={() => sliderRef.current?.prev()} type="button">
              {' '}
              <SVGAngleLeft />
            </button>
          )}
          {sliderState && sliderState.isScrollable && (
            <button className={clsx(styles.arrow, styles.right)} onClick={() => sliderRef.current?.next()} type="button">
              {' '}
              <SVGAngleRight />
            </button>
          )}
          {sliderState && sliderState.isScrollable && (
            <input
              className={styles.range}
              max={sliderState.rangeMax}
              min={sliderState.rangeMin}
              onChange={noop}
              type="range"
              value={sliderState.rangeValue}
            />
          )}
        </div>
      </div>
      {prompts && prompts.length !== 0 && (
        <PromptDialog
          isLoading={isLoading}
          onClose={reset}
          onSubmit={addToCart}
          prompts={prompts}
          promptsOrigin={variables}
        />
      )}
    </>
  )
}

